/**
 * PosteingangPage - the route screen behind /posteingang (epic B2).
 *
 * The Eingangsliste over all four channels (Navigationskonzept V3.0 §2.2, Bereich 5).
 *
 * URL to props plus page chrome. The tab and Kanal filter are search params so a
 * filtered Posteingang stays linkable, defaulted here so a plain /posteingang URL stays
 * clean. Filter changes replace the history entry - flipping a chip is not navigation.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { posteingangRoute } from '../../routeTree.gen';
import { PosteingangScreen } from './PosteingangScreen';
import { messageForErrorReason } from '../mailbox/connect-return';
import { qk } from '../../lib/query-keys';

type Toast = { kind: 'success' | 'error'; message: string } | null;

export function PosteingangPage() {
  const navigate = useNavigate();
  const { state: stateParam, kanal: kanalParam } = posteingangRoute.useSearch();
  const state = stateParam ?? 'offen';
  const kanal = kanalParam ?? null;

  // The mailbox-connect OAuth return (api MC4_CONNECT_REDIRECT_PATH = /posteingang) lands here now
  // that /kommunikation is gone (Jevin #37): toast the result, refresh the connection list for when
  // the broker opens Postfächer, and strip the param so a reload does not toast again. Mount-only.
  const qc = useQueryClient();
  const [toast, setToast] = useState<Toast>(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get('mailbox');
    if (result !== 'connected' && result !== 'error') return;
    if (result === 'connected') {
      setToast({ kind: 'success', message: 'Postfach erfolgreich verbunden.' });
      void qc.invalidateQueries({ queryKey: qk.mailboxConnections() });
    } else {
      setToast({ kind: 'error', message: messageForErrorReason(params.get('reason')) });
    }
    const t = setTimeout(() => setToast(null), 4000);
    window.history.replaceState({}, '', window.location.pathname);
    return () => clearTimeout(t);
  }, [qc]);

  return (
    <div className="px-6 py-5">
      {toast != null && (
        <div
          role={toast.kind === 'error' ? 'alert' : 'status'}
          aria-live={toast.kind === 'error' ? 'assertive' : 'polite'}
          className={
            toast.kind === 'error'
              ? 'mb-4 rounded-md border border-status-danger-border bg-status-danger-bg px-3 py-2 text-sm text-status-danger'
              : 'mb-4 rounded-md border border-status-success-border bg-status-success-bg px-3 py-2 text-sm text-status-success'
          }
        >
          {toast.message}
        </div>
      )}
      <h1 className="display-title">Posteingang</h1>
      {/* §5.5: the Posteingang is the Eingangsschicht of ALL incoming channels —
          naming them is what separates this Bereich from the Kommunikation mail view. */}
      <p className="mt-1 text-sm text-muted-foreground">
        Alle Eingangskanäle · E-Mail, Scanning-Center, Upload, Kundenportal
      </p>

      <div className="mt-4">
        <PosteingangScreen
          state={state}
          kanal={kanal}
          onStateChange={(next) =>
            void navigate({
              to: '/posteingang',
              search: { state: next, ...(kanal ? { kanal } : {}) },
              replace: true,
            })
          }
          onKanalChange={(next) =>
            void navigate({
              to: '/posteingang',
              search: { state, ...(next ? { kanal: next } : {}) },
              replace: true,
            })
          }
          // Row and Betreff → the Bearbeitungsmaske (the Eingang and its tasks).
          onOpenEingang={(commId) =>
            void navigate({ to: '/posteingang/$id', params: { id: commId } })
          }
          onOpenKunde={(_subjectType, id) => void navigate({ to: '/kunden/$id', params: { id } })}
        />
      </div>
    </div>
  );
}
